import React, { FC, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Paper, Divider, Button } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useSnackbar } from 'notistack';
import { CustomTrainingForm, TrainingTeamAssignee, Shot } from '../../../models/training.models';
import {
  TextFieldComponent,
  NumberFieldComponent,
  EnumFieldComponent,
  RatingFieldComponent,
  FullTeamNumberFieldComponent,
  TargetRecordingFieldComponent,
} from '../../training-form-fields';
import useService from '../../../hooks/service.hook';
import TrainingsService from '../../../services/trainings.service';
import { palette } from '../../../theme';

interface FormsTabProps {
  form: CustomTrainingForm | null;
  loading: boolean;
  teamMembers?: TrainingTeamAssignee[];
  disabled?: boolean;
  trainingId: number;
}

const FormsTab: FC<FormsTabProps> = ({ form, loading, teamMembers = [], disabled = false, trainingId }) => {
  const trainingsService = useService(TrainingsService);
  const { enqueueSnackbar } = useSnackbar();

  // State to manage form field values
  const [formValues, setFormValues] = useState<Record<number, any>>({});
  const [responseLoading, setResponseLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  // Fetch existing form response when component mounts or form changes
  useEffect(() => {
    if (form && trainingId) {
      fetchFormResponse();
    } else {
      // Clear form values if no form
      setFormValues({});
    }
  }, [form, trainingId]);

  const fetchFormResponse = async () => {
    if (!form || !trainingId) return;

    setResponseLoading(true);
    try {
      const response = await trainingsService.getFormResponse(trainingId);
      const responseData = response.data.data;

      if (responseData && responseData.responses) {
        // Convert API response format to UI format
        const convertedValues: Record<number, any> = {};

        Object.keys(responseData.responses).forEach((fieldIdStr) => {
          const fieldId = parseInt(fieldIdStr, 10);
          const field = form.fields.find((f) => f.field_id === fieldId);

          if (!field) return;

          const apiValue = responseData.responses[fieldIdStr];

          // Convert based on field type
          switch (field.field_type) {
            case 'enum':
              // API returns array (supports multi-select), UI expects array
              if (apiValue === null || apiValue === undefined || apiValue === '') {
                convertedValues[fieldId] = [];
              } else {
                // API should return array, but handle legacy single string values if needed
                convertedValues[fieldId] = Array.isArray(apiValue) ? apiValue : [apiValue];
              }
              break;
            case 'full_team_number': {
              // API returns Record<string, number>, UI expects Record<number, number>
              const convertedTeamNumber: Record<number, number> = {};
              Object.keys(apiValue).forEach((athleteIdStr) => {
                const athleteId = parseInt(athleteIdStr, 10);
                convertedTeamNumber[athleteId] = apiValue[athleteIdStr];
              });
              convertedValues[fieldId] = convertedTeamNumber;
              break;
            }
            case 'target_recording': {
              // API returns Record<string, Shot[]>, UI expects Record<number, Shot[]>
              if (typeof apiValue === 'object' && apiValue !== null) {
                const convertedTargetRecording: Record<number, Shot[]> = {};
                Object.keys(apiValue).forEach((athleteIdStr) => {
                  const athleteId = parseInt(athleteIdStr, 10);
                  convertedTargetRecording[athleteId] = apiValue[athleteIdStr] as Shot[];
                });
                convertedValues[fieldId] = convertedTargetRecording;
              }
              break;
            }
            default:
              // text, number, rating - use value as is
              convertedValues[fieldId] = apiValue;
              break;
          }
        });

        setFormValues(convertedValues);
      }
    } catch (error: any) {
      // Silently handle 404 (no response exists yet)
      if (error?.response?.status !== 404) {
        enqueueSnackbar('Failed to load form response. Please try again.', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } finally {
      setResponseLoading(false);
    }
  };

  const handleFieldChange = (fieldId: number, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!form || !trainingId) return;

    setSubmitting(true);
    try {
      // Convert UI format to API format
      const apiResponses: Record<string, any> = {};

      Object.keys(formValues).forEach((fieldIdStr) => {
        const fieldId = parseInt(fieldIdStr, 10);
        const field = form.fields.find((f) => f.field_id === fieldId);

        if (!field) return;

        const uiValue = formValues[fieldId];

        // Convert based on field type
        switch (field.field_type) {
          case 'enum':
            // UI has array, API expects array (supports multi-select)
            if (Array.isArray(uiValue)) {
              // Send array even if empty (empty array means no options selected)
              apiResponses[fieldIdStr] = uiValue;
            }
            // Skip if not an array
            break;
          case 'text':
            // Skip empty strings
            if (uiValue === null || uiValue === undefined || uiValue === '') {
              return;
            }
            apiResponses[fieldIdStr] = uiValue;
            break;
          case 'full_team_number':
            // UI has Record<number, number>, API expects Record<string, number>
            if (typeof uiValue === 'object' && uiValue !== null) {
              const convertedTeamNumber: Record<string, number> = {};
              Object.keys(uiValue).forEach((athleteIdStr) => {
                const athleteId = parseInt(athleteIdStr, 10);
                const value = uiValue[athleteId];
                if (value !== null && value !== undefined) {
                  convertedTeamNumber[athleteIdStr] = value;
                }
              });
              // Only include if there are values
              if (Object.keys(convertedTeamNumber).length > 0) {
                apiResponses[fieldIdStr] = convertedTeamNumber;
              }
            }
            break;
          case 'target_recording':
            // UI has Record<number, Shot[]>, API expects Record<string, Shot[]>
            if (typeof uiValue === 'object' && uiValue !== null) {
              const convertedTargetRecording: Record<string, Shot[]> = {};
              Object.keys(uiValue).forEach((athleteIdStr) => {
                const athleteId = parseInt(athleteIdStr, 10);
                const shots = uiValue[athleteId];
                if (shots && Array.isArray(shots) && shots.length > 0) {
                  convertedTargetRecording[athleteIdStr] = shots;
                }
              });
              // Only include if there are values
              if (Object.keys(convertedTargetRecording).length > 0) {
                apiResponses[fieldIdStr] = convertedTargetRecording;
              }
            }
            break;
          case 'number':
          case 'rating':
            // Skip null/undefined values
            if (uiValue === null || uiValue === undefined) {
              return;
            }
            apiResponses[fieldIdStr] = uiValue;
            break;
          default:
            // Fallback - should not reach here
            break;
        }
      });

      await trainingsService.submitFormResponse(trainingId, apiResponses);

      enqueueSnackbar('Form response submitted successfully!', {
        variant: 'success',
        autoHideDuration: 2000,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to submit form response. Please try again.';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: any) => {
    const value = formValues[field.field_id];

    switch (field.field_type) {
      case 'text':
        return (
          <TextFieldComponent
            field={field}
            value={value || ''}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <NumberFieldComponent
            field={field}
            value={value ?? null}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
          />
        );

      case 'enum':
        return (
          <EnumFieldComponent
            field={field}
            value={value || []}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
          />
        );

      case 'rating':
        return (
          <RatingFieldComponent
            field={field}
            value={value ?? null}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
          />
        );

      case 'full_team_number':
        return (
          <FullTeamNumberFieldComponent
            field={field}
            value={value || {}}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
            teamMembers={teamMembers}
          />
        );

      case 'target_recording':
        return (
          <TargetRecordingFieldComponent
            field={field}
            value={value || {}}
            onChange={(val) => handleFieldChange(field.field_id, val)}
            disabled={disabled}
            teamMembers={teamMembers}
          />
        );

      default:
        return null;
    }
  };

  if (loading || responseLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          p: 3,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!form) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          minHeight: '200px',
        }}
      >
        <DescriptionIcon sx={{ fontSize: 48, color: palette.text.disabled, mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No Form Attached
        </Typography>
        <Typography variant="body2" color="textSecondary" textAlign="center">
          No post-training form is attached to this workout
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper variant="outlined" sx={{ p: 3, bgcolor: palette.background.subtle }}>
        <Typography variant="h5" gutterBottom>
          {form.name}
        </Typography>
        {form.description && (
          <Typography variant="body1" color="textSecondary" paragraph sx={{ mb: 3 }}>
            {form.description}
          </Typography>
        )}
        <Divider sx={{ my: 3 }} />

        {/* Render interactive fields */}
        <Box sx={{ mt: 4 }}>
          {form.fields.map((field) => (
            <Box key={field.field_id}>{renderField(field)}</Box>
          ))}
        </Box>

        {/* Submit Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={disabled || submitting || responseLoading}
            sx={{
              minWidth: '120px',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {submitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <span>Submitting...</span>
              </Box>
            ) : (
              'Submit'
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormsTab;
